/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div>
    <form
      aria-labelledby="urlheader"
      @submit.prevent="upload"
    >
      <h2
        id="urlheader"
      >
        Load Audio From a URL
      </h2>
      Zoom Share Links, YouTube and others supported.<br><br>
      <assignment-select
        v-if="showAssignmentSelect"
        @assignmentChosen="assignmentChosen"
      />
      <div class="form-group">
        <label
          for="urlTitle"
          class="field-label"
        >
          TITLE
        </label>
        <input
          id="urlTitle"
          v-model.trim="formValues.title"
          type="text"
          class="form-control"
          placeholder="title"
          name="title"
          required
          maxlength="50"
        >
      </div>
      <div
        v-if="speakerGroups.length > 1 && !assignmentsEnabled"
        class="form-group"
      >
        <label
          for="groupSelector3"
          class="field-label"
        >
          <span v-if="orgType == 'edu'">
            CLASS
          </span>
          <span v-if="orgType != 'edu'">
            GROUP
          </span>
        </label>
        <select
          id="groupSelector3"
          v-model="selectedGroup"
          class="form-control"
        >
          <option
            v-for="group in speakerGroups"
            :value="group"
          >
            {{ group.name }}
          </option>
        </select>
      </div>
      <div class="form-group">
        <label
          for="urlURL"
          class="field-label"
        >
          URL
        </label>
        <input
          id="urlURL"
          v-model.trim="formValues.audio"
          type="text"
          class="form-control"
          placeholder="https://..."
          name="url"
          required
          pattern="https?://.+"
          @change="checkUrl"
        >
        <p class="help-block small">
          Many formats accepted. Max 200 MB, 15min
        </p>
      </div>

      <speaker-select
        v-if="showAlgorithmSelect"
        @algorithmChosen="algorithmChosen"
      />

      <assignment-sharing
        v-if="assignmentsEnabled && recordingType !== 'private'"
        ref-num="3"
        @sharingChanged="sharingChanged"
      />

      <p v-show="!showAdvanced">
        <a
          href="#"
          @click.prevent="showAdvanced = true"
        >
          <small>Audio Clipping Controls</small>
        </a>
      </p>
      <div v-show="showAdvanced">
        <upload-trimming
          :trim="formValues.trim"
          count="2"
        />
      </div>
      <debug-recordings
        v-if="$store.getters.org && $store.getters.org.settings && $store.getters.org.settings.Debug_Recording_Options"
        @devFormData="loadDevFormData"
      />
      <loading-button
        type="submit"
        :loading="saving"
      >
        Save
      </loading-button>
    </form>
  </div>
</template>

<style lang="less" scoped>
  h2 {
    margin: 5px 0 15px 0;
  }
  #urlTitle, #urlURL, #groupSelector3 {
    max-width: 300px;
  }

  input[type=range] {
    margin-top: 4px;
  }
</style>

<script>
import service from '../../services/recordings';
import UploadTrimming from '../forms/UploadTrimming';
import AssignmentRecording from '../specials/AssignmentRecording';
import AssignmentSelect from '../specials/AssignmentSelect';
import AssignmentSharing from '../specials/AssignmentSharing';
import SpeakerSelect from '../specials/SpeakerSelect';
import DebugRecordings from '../specials/DebugRecordings';
import util from '../utils/util';

const ZoomWarningMsg = 'Zoom Share Links are not supported. ' +
                        'To process a Zoom video, please either use our Zoom integration or download the video then upload it via the Upload File tab.';

export default {
  components: {SpeakerSelect, AssignmentSelect, UploadTrimming, AssignmentSharing, DebugRecordings},
  mixins: [AssignmentRecording],
  data() {
    return {
      saving: false,
      showAdvanced: false,
      selectedGroup: null,
      formValues: {
        title: '',
        audio: '',
        source: 'ui url form',
        submitted: false,
        feedbackRequested: false,
        trim: {
          from: 0,
          to: -1
        },
        algorithm: null,
      }
    };
  },
  computed: {
    speakerGroups() {
      let speakerGroups = this._.filter(this.$store.getters.groups, group => {
        return this._.includes(group.roles, 'speaker') && group.membershipStatus === 'active';
      });
      speakerGroups = this._.sortBy(
        speakerGroups, g => -new Date(g.createdDate).getTime()
      );

      // Set default selectedGroup
      if (!this.selectedGroup && speakerGroups.length) {
        this.selectedGroup = speakerGroups[0];
      }

      return speakerGroups;
    },
    isFreeTrial() {
      return !!(this.user && this.user.metadata && this.user.metadata.freeTrial);
    },
    orgType() {
      return this.$store.getters.org && this.$store.getters.org.type;
    },
    user() {
      return this.$store.getters.user;
    },
    org() {
      return this.$store.getters.org;
    },
    videoEnabled() {
      return (this.org && typeof this.org.settings !== 'undefined' && this.org.settings.Video);
    },
  },
  created() {
    this.populateAssignmentAndTitle();
    this.ZOOM_WARNING_MSG = ZoomWarningMsg;
  },
  methods: {
    checkUrl() {
      if (!this.videoEnabled) return true;

      const url = this.formValues.audio || '';
      const fileExtension = util.getFileExtension(url);

      if (fileExtension && util.isIncompatibleVideo(fileExtension)) {
        return alert(
          'Only video files in mp4 format can be displayed. You will still receive an analysis ' +
          'for this recording, however please note a video playback screen will not be available ' +
          'for this file.'
        );
      }

      if (!this.checkForShareLink()) {
        return alert(this.ZOOM_WARNING_MSG);
      }

      return alert(
        'Some video types cannot be displayed in VoiceVibes.  You will still receive an analysis ' +
        'for this recording, however please note a video playback screen may not be available ' +
        'for this file.'
      );
    },
    reset() {
      this.formValues.title = '';
      this.formValues.algorithm = null;
      this.formValues.submitted = false;
      this.formValues.feedbackRequested = false;
      this.formValues.audio = '';
      this.formValues.isVideo = false;
      this.formValues.trim = {
        from: 0,
        to: -1
      };
      this.showAdvanced = false;
    },
    checkForShareLink() {
      const url = this.formValues.audio || '';
      const regex = /https:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.zoom\.[a-z]{2,4}\/rec\/share\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;
      let pass = true;

      if (regex.test(url)) {
        pass = false;
      }

      return pass;
    },
    upload() {

      if (!this.checkForShareLink()) {
        return alert(this.ZOOM_WARNING_MSG);
      }

      this.saving = true;

      // Check for video
      if (this.videoEnabled) {
        // Assume they're uploading a video. It won't matter if they're not.
        this.formValues.isVideo = true;
      }

      const formData = this.getFormData();

      service.post(formData)
        .then(recording => {
          if (this.isFreeTrial) {
            // Redirect to bounce page
            return document.location.href = 'https://www.myvoicevibes.com/free_trial_thanks.html';
          }
          const recordingId = recording.recordingId;
          this.addRecordingToAssignment(recordingId);

          return this.$store.dispatch('listRecordings')
            .then(() => {
              this.reset();
              this.recordingUploaded(recordingId, formData);
            });
        })
        .catch(() => {
          this.$store.dispatch('notify', {
            type: 'error',
            subject: 'URL Upload Error',
            text: `Error processing the URL. Please ensure the URL you entered is correct.
                You may also wish to reload the page then resubmit the URL.`
          });
        })
        .finally(() => {
          this.saving = false;
        });
    
    }
  }
};
</script>
